import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  const companyName = "OnRounds AI";

  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Notice of Patient Rights Policy
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Notice of Patient Rights Policy</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Box mb={2}>
            <Typography variant="body1" paragraph>
              This policy establishes the rights of patients regarding their Protected Health Information (PHI) under
              the Health Insurance Portability and Accountability Act (HIPAA).
            </Typography>
            <Typography variant="body1" paragraph>
              It ensures patients are informed about their privacy rights and how their information is used and
              protected by {companyName}.
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="h6" gutterBottom>
              Patient Rights
            </Typography>
          </Box>

          <Box mb={0}>
            <Typography variant="body1">
              This policy applies to all forms of PHI managed by {companyName}, including electronic, paper, and oral
              communications. All employees, contractors, and business associates who have access to PHI are required to
              comply with this policy.
            </Typography>
          </Box>

          <Box mb={2}>
            <List>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Access Their PHI:</b> Patients can inspect and obtain a copy of their PHI contained in a
                    designated record set for as long as the PHI is maintained. Requests for access must be submitted in
                    writing. {companyName} will respond within 30 days.
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Request an Amendment of their PHI:</b> If a patient believes the PHI maintained by {companyName}{" "}
                    is incorrect or incomplete, they may request an amendment. Requests for amendments must be submitted
                    in writing, along with a reason supporting the request.
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Receive an Accounting of Disclosures:</b> Patients have the right to request an accounting of
                    certain disclosures of their PHI made by {companyName}. The accounting will cover up to the six
                    years prior to the request date and exclude disclosures for treatment, payment, and healthcare
                    operations.
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Request Restrictions on Certain Uses and Disclosures:</b> Patients may request restrictions on
                    the use and disclosure of their PHI for treatment, payment, or healthcare operations, as well as to
                    certain persons. While {companyName} will consider all requests, it is not required to agree to a
                    requested restriction unless it involves a disclosure to a health plan for purposes of payment or
                    healthcare operations and the PHI pertains solely to a healthcare item or service for which the
                    patient has paid out of pocket in full.
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Request Confidential Communications:</b> Patients have the right to request, and {companyName}{" "}
                    will accommodate reasonable requests for, receiving communications of PHI by alternative means or at
                    alternative locations.
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Receive a Paper Copy of the Notice of Privacy Practices:</b> Patients have the right to a paper
                    copy of {companyName}'s Notice of Privacy Practices upon request.
                  </Typography>
                </Box>
              </ListItem>
            </List>
          </Box>

          <Box>
            <Typography variant="h6">Procedures for Exercising Rights</Typography>
          </Box>
          <Box mb={2}>
            <List>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Submitting Requests:</b> All requests related to patient rights as outlined above must be
                    submitted in writing to the designated Privacy Officer at {companyName}. Necessary forms and further
                    instructions can be obtained from [ nnolasco@onrounds.com ].
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Response Time:</b> {companyName} will respond to requests in accordance with HIPAA requirements,
                    generally within 30 days of receipt. If the request cannot be processed within this period, an
                    extension of an additional 30 days may be invoked with written notice to the patient.
                  </Typography>
                </Box>
              </ListItem>
              <ListItem alignItems="flex-start">
                <Box display="flex" flexDirection="column">
                  <Typography component="span">
                    <b>Denials:</b> If a request for access, amendment, or restriction is denied, {companyName} will
                    provide a written denial notice stating the basis for the denial, a description of how the patient
                    can exercise their review rights (if applicable), and a description of how the patient can complain
                    to the Secretary of the U.S. Department of Health and Human Services..
                  </Typography>
                </Box>
              </ListItem>
            </List>
          </Box>

          <Box mb={2}>
            <Typography variant="h6">Complaints</Typography>
          </Box>
          <Box mb={2}>
            <Typography paragraph>
              Patients who believe their privacy rights have been violated may file a complaint with {companyName}'s
              Privacy Officer or directly with the Secretary of the U.S. Department of Health and Human Services.
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="h6">Contact Information</Typography>
          </Box>

          <Box mb={2}>
            <Typography paragraph>
              For further information about how to exercise these rights or to file a complaint, please contact: [
              nnolasco@onrounds.com ]
            </Typography>
          </Box>

          <Box mb={2}>
            <Typography variant="h6">Policy Review and Modification</Typography>
          </Box>

          <Box mb={2}>
            <Typography paragraph>
              This policy is subject to regular review and updating to ensure compliance with HIPAA regulations and to
              reflect changes in our practices. Patients will be notified of significant changes to this policy through
              updates to the Notice of Privacy Practices.
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
